import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import styled from "styled-components";
import { Application } from "@domain/application/application";
import { ApplicationDraft } from "@domain/application/applicationDraft";
import { ApplicationEditionStep } from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorController";
import { theme } from "@ui/styles/theme";
import { mobile } from "@ui/styles/utils";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { row, stack } from "@ui/components/layout";
import { DocumentSectionSummaryView } from "@ui/components/application/section/documentSectionSummaryView";
import { PersonalInformationSummaryView } from "@ui/components/application/section/personalInformationSummaryView";
import { StudyChoiceSummaryView } from "@ui/components/application/section/studyChoiceSummaryView";
import { VideoTeasingSectionSummaryView } from "@ui/components/application/section/videoTeasingSectionSummaryView";

export interface SummaryViewProps extends React.HTMLAttributes<HTMLDivElement> {
	application: Application | ApplicationDraft;
	renderIcons?: (step?: ApplicationEditionStep) => JSX.Element;
	isForApplicationProcess?: boolean;
}

export const SummarySectionView: React.FC<SummaryViewProps> = ({
	application,
	isForApplicationProcess = true,
	renderIcons,
	...htmlProps
}) => {
	return (
		<Container {...htmlProps}>
			<MainContainer>
				<ContainerStep key="STUDY_CHOICES" linearColor={theme.colors.primaryGradientLight}>
					<Step linearColor={theme.colors.primaryGradient}>
						{renderIcons?.("STUDY_CHOICES")}
						<StyledPrimaryBold>
							{I18n.t(`application.step.${"STUDY_CHOICES".toLocaleLowerCase()}.title`)}
						</StyledPrimaryBold>
					</Step>
					<InfosStyle>
						<StudyChoiceSummaryView application={application} />
					</InfosStyle>
				</ContainerStep>
				<ContainerStep key="PERSONAL_INFORMATIONS" linearColor={theme.colors.secondaryGradientLight}>
					<Step linearColor={theme.colors.secondaryGradient}>
						{renderIcons?.("PERSONAL_INFORMATIONS")}
						<StyledPrimaryBold>
							{I18n.t(`application.step.${"PERSONAL_INFORMATIONS".toLocaleLowerCase()}.title`)}
						</StyledPrimaryBold>
					</Step>
					<InfosStyle>
						<PersonalInformationSummaryView application={application} />
					</InfosStyle>
				</ContainerStep>
			</MainContainer>
			<MainContainer count={isForApplicationProcess ? 2 : 1}>
				<ContainerStep key="UPLOAD_DOCUMENTS" linearColor={theme.colors.thirdGradientLight}>
					<Step linearColor={theme.colors.thirdGradient}>
						{renderIcons?.("UPLOAD_DOCUMENTS")}
						<StyledPrimaryBold>
							{I18n.t(`application.step.${"UPLOAD_DOCUMENTS".toLocaleLowerCase()}.title`)}
						</StyledPrimaryBold>
					</Step>
					<InfosStyle>
						<DocumentSectionSummaryView application={application} />
					</InfosStyle>
				</ContainerStep>
				{isForApplicationProcess && (
					<ContainerStep key="VIDEO_STORY" linearColor={theme.colors.fourthGradientLight}>
						<Step linearColor={theme.colors.fourthGradient}>
							{renderIcons?.("VIDEO_STORY")}
							<StyledPrimaryBold>
								{I18n.t(`application.step.${"VIDEO_STORY".toLocaleLowerCase()}.title`)}
							</StyledPrimaryBold>
						</Step>
						<InfosStyle>
							<VideoTeasingSectionSummaryView application={application} />
						</InfosStyle>
					</ContainerStep>
				)}
			</MainContainer>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`;

const MainContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "count",
})<{ count?: number }>`
	@media ${mobile} {
		${stack(20)}
	}
	display: grid;
	grid-template-columns: ${({ count }) => `repeat(${count ?? 2}, 1fr);`} 
	grid-gap: 40px;
`;

const Step = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "linearColor",
})<{ linearColor: string }>`
	${row(10, "flex-start", "center")}
	position: absolute;
	top: 0;
	left: 0;
	background: ${({ linearColor }) => `linear-gradient(${linearColor})`};
	width: 100%;
	padding: 30px 10px;
	border-radius: 13px;
`;

const ContainerStep = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => prop !== "linearColor",
})<{ linearColor: string }>`
	background: ${({ linearColor }) => `linear-gradient(${linearColor})`};
	padding: 20px 10px 20px 10px;
	position: relative;
	border: none !important;
	width: 100%;
`;

const InfosStyle = styled.div`
	margin-left: 0px;
	margin-top: 100px;
`;

const StyledPrimaryBold = styled(PrimaryBold)`
	color: ${theme.colors.white} !important;
`;
